import { encode, decode } from "punycode-esm";
import wikiEncoding from "wiki-article-name-encoding";
import { useLocalStorage } from "@vueuse/core";

export function round(value, decimals = 0) {
  return Math.round(value * 10 ** decimals) / 10 ** decimals;
}

const MAX_FRACTION_DIGITS = 20;

export function numberFormatted(number, options = {}) {
  const { locale = "en-US", decimals = 0, avoidZero = false } = options;
  if (!isNumber(number)) {
    return null;
  }
  let value = round(number, decimals);
  let fractionDigits = decimals;

  // if set, add decimals until a non-zero number no longer rounds down to 0
  if (value == 0 && number != 0 && avoidZero) {
    while (value == 0 && fractionDigits < MAX_FRACTION_DIGITS) {
      fractionDigits++;
      value = round(number, fractionDigits);
    }
  }
  // avoid "-0" for small negative numbers
  if (value == 0) {
    value = 0;
  }
  return value.toLocaleString(locale, {
    useGrouping: true,
    maximumFractionDigits: fractionDigits,
  });
}

export function isAsync(fn) {
  return fn.constructor.name === "AsyncFunction";
}

export function headingDirectionFormatted(number) {
  const conversion = [
    "N",
    "NNE",
    "NE",
    "ENE",
    "E",
    "ESE",
    "SE",
    "SSE",
    "S",
    "SSW",
    "SW",
    "WSW",
    "W",
    "WNW",
    "NW",
    "NNW",
  ];
  if (!isNumber(number)) {
    return null;
  }
  const angle = Math.floor(number / 22.5 + 0.5);
  return conversion[((angle % 16) + 16) % 16];
}

// distinguish between null/undefined and 0
export function isNumber(value) {
  return typeof value === "number" && !isNaN(value) && isFinite(value);
}

export function isGeolocationError(error) {
  return typeof GeolocationPositionError === "function" && unref(error) instanceof GeolocationPositionError;
}

export function decodeUrlTitle(text) {
  if (!text) {
    return text;
  }
  // if the tet doesn't end with a dash, try to decode it as a wiki title
  if (!text.includes("-")) {
    return wikiEncoding.decode(text);
  }
  return decode(text).replace(/--/g, ", ").replace(/-/g, " ").replace(/_/g, ".");
}

export function encodeUrlTitle(text) {
  if (!text) {
    return text;
  }
  return encode(text.replace(/\./g, "_")).replace(/,\W/g, "--").replace(/\W/g, "-");
}

export function decodeUrlSlug(name) {
  // replace dashes with spaces and capitalize
  return name.replace(/-/g, " ").replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

export function encodeUrlSlug(name) {
  return name.replace(/\s/g, "-").toLowerCase();
}

export function boilingPointForElevation(altitude, units = "metric") {
  // Constants
  const seaLevelPressure = 101325; // Pa
  const temperatureLapseRate = 0.0065; // K/m
  const seaLevelTemp = 288.15; // K
  const gasConstant = 8.314; // J/(mol*K)
  const molarMassEarthAir = 0.029; // kg/mol
  const gravity = 9.80665; // m/s^2

  // Calculate pressure at altitude using the barometric formula
  const pressureAtAltitude =
    seaLevelPressure *
    Math.pow(
      1 - (temperatureLapseRate * altitude) / seaLevelTemp,
      (gravity * molarMassEarthAir) / (gasConstant * temperatureLapseRate)
    );

  // Convert pressure to mmHg
  const pressureAtAltitudeMmHg = pressureAtAltitude * 0.00750062;

  // Calculate boiling point at altitude using the rule of thumb approximation
  const boilingPointAtAltitude = 100 - (760 - pressureAtAltitudeMmHg) * 0.045;

  // Convert to Fahrenheit if necessary
  if (units === "imperial") {
    return boilingPointAtAltitude * 1.8 + 32;
  }

  return boilingPointAtAltitude;
}

export function isBot() {
  return typeof window === "undefined" || /bot|headless|lighthouse|speed insights/i.test(window.navigator.userAgent);
}

export function isClient() {
  return typeof window !== "undefined";
}

export function isEqual(a, b) {
  return JSON.stringify(a) == JSON.stringify(b);
}

export function arrayToTextList(arr, andOr = "and") {
  if (arr.length === 0) {
    return "";
  } else if (arr.length === 1) {
    return arr[0];
  } else if (arr.length === 2) {
    return arr.join(` ${andOr} `);
  } else {
    return `${arr.slice(0, -1).join(", ")}, ${andOr} ${arr.at(-1)}`;
  }
}

export function countySubdivisionLink(state) {
  const type = encodeUrlSlug(state?.countySubdivisions?.length ? state.countySubdivisions[0].one : "township");
  return `/what-${type}-am-i-in`;
}

export function useSafeLocalStorage(key, initialValue, options = {}) {
  try {
    return useLocalStorage(key, initialValue, options);
  } catch {
    return ref(initialValue);
  }
}

export function enableLocationDisabledDialog() {
  window.dispatchEvent(new CustomEvent("location-disabled"));
}

export function toISODate(date) {
  date = new Date(date);
  if (isNaN(date.getTime())) {
    return null;
  }
  return (
    date.getUTCFullYear() +
    "-" +
    (date.getUTCMonth() + 1).toString().padStart(2, "0") +
    "-" +
    date.getUTCDate().toString().padStart(2, "0")
  );
}
